import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { FaUser, FaEnvelope, FaPhone, FaPaperPlane, FaMapMarkerAlt, FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaBriefcase, FaChevronDown, FaWhatsapp } from 'react-icons/fa';
import PremiumButton from '../components/ui/PremiumButton';

const servicesList = [
  'Social Media Management',
  'Graphic Design & Branding',
  'Video Production & Editing',
  'Web Development & SEO',
  'Paid Advertising',
  'Personal Brand Management'
];

export default function ContactPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', role: '', services: [], message: ''
  });

  const roleOptions = [
    // Healthcare
    'Doctor / Clinic',
    'Hospital / Healthcare Brand',
    'Pharmacy / Medstore',
    // Beauty & Wellness
    'Makeup Artist / MUA',
    'Salon / Spa / Beauty Studio',
    'Fitness Trainer / Gym',
    // Food & Hospitality
    'Restaurant / Café / Cloud Kitchen',
    'Catering / Events Business',
    // Education
    'Coaching Institute / Tutor',
    'Ed-Tech / Online Course Creator',
    // Fashion & Lifestyle
    'Fashion Brand / Boutique',
    'Influencer / Content Creator',
    // Real Estate & Local Business
    'Real Estate Agent / Builder',
    'Local Shop / Retailer',
    // Professional Services
    'CA / Lawyer / Consultant',
    'Travel Agency / Tour Operator',
    // Tech & Startups
    'Startup / Tech Company',
    'E-Commerce Brand',
    // Other
    'Other',
  ];

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.services.length === 0) {
      alert('Please select at least one service.');
      return;
    }

    try {
      // 1. Store in Firebase
      await addDoc(collection(db, 'contacts'), {
        ...formData,
        createdAt: serverTimestamp()
      });

      // 2. Create WhatsApp message
      const whatsappMessage = `*New Lead from BottomFunnel*%0A` +
        `*Name:* ${formData.name}%0A` +
        `*Email:* ${formData.email}%0A` +
        `*Phone:* ${formData.phone}%0A` +
        `*Industry:* ${formData.role}%0A` +
        `*Services:* ${formData.services.join(', ')}%0A` +
        `*Message:* ${formData.message}`;

      const whatsappUrl = `https://wa.me/919473448626?text=${whatsappMessage}`;

      // 3. Open WhatsApp
      window.open(whatsappUrl, '_blank');

      alert('Thank you for contacting us! Your details have been saved and WhatsApp has been opened.');
      navigate('/');
    } catch (error) {
      console.error('Error adding document: ', error);
      alert('Something went wrong. Please try again or contact us via email/WhatsApp.');
    }
  };

  const toggleService = (service) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  return (
    <div className="pt-32 pb-20 px-4 min-h-screen relative overflow-hidden flex items-center">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent pointer-events-none" />
      
      <div className="max-w-7xl mx-auto w-full relative z-10">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-16">
          <span className="inline-block px-6 py-2 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-full text-sm font-semibold text-cyan-400 mb-6 backdrop-blur-sm">
            Contact Us
          </span>
          <h1 className="text-5xl md:text-7xl font-black mb-6">
            Get In <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Touch</span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            We'd love to hear from you. Fill out the form or use our contact information below.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-8">
          {/* Contact Info Side */}
          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2 space-y-8 bg-gradient-to-br from-gray-50/90 dark:from-gray-900/90 to-white/90 dark:to-black/90 p-8 md:p-10 rounded-3xl border border-gray-200 dark:border-gray-800 backdrop-blur-md">
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Contact Information</h3>
            
            <div className="space-y-6">

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center flex-shrink-0 border border-cyan-500/20">
                  <FaMapMarkerAlt className="text-cyan-400 text-xl" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Our Location</h4>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">SKMCH GATE NO:-1 ,<br/>Muzaffarpur, Bihar .<br/>842004</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center flex-shrink-0 border border-orange-500/20">
                  <FaEnvelope className="text-orange-400 text-xl" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Email Us</h4>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    <a href="mailto:replybottomfunnel@gmail.com" className="hover:text-cyan-500 transition-colors">
                      replybottomfunnel@gmail.com
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center flex-shrink-0 border border-green-500/20">
                  <FaWhatsapp className="text-green-500 text-xl" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Call or WhatsApp Us</h4>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    <a href="https://wa.me/919473448626" target="_blank" rel="noopener noreferrer" className="hover:text-green-500 transition-colors">
                      +91 9473448626
                    </a>
                    <br/>Mon - Fri, 9am - 6pm
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-8 mt-8 border-t border-gray-200 dark:border-gray-800">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Follow Us</h4>
              <div className="flex gap-4 flex-wrap">
                {[
                  { icon: FaFacebook, color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/20', link: '#' },
                  { icon: FaTwitter, color: 'text-sky-400', bg: 'bg-sky-400/10', border: 'border-sky-400/20', link: '#' },
                  { icon: FaInstagram, color: 'text-pink-500', bg: 'bg-pink-500/10', border: 'border-pink-500/20', link: '#' },
                  { icon: FaLinkedin, color: 'text-blue-600', bg: 'bg-blue-600/10', border: 'border-blue-600/20', link: '#' },
                ].map((s, i) => (
                  <motion.a key={i} href={s.link} whileHover={{ y: -5, scale: 1.1 }}
                    className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.bg} border ${s.border} transition-colors hover:bg-white/10`}>
                    <s.icon className={`text-xl ${s.color}`} />
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Google Maps Preview Card */}
            <motion.a
              href="https://maps.app.goo.gl/aa59VJh6RQ1MaGZ66"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="block rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 group cursor-pointer"
            >
              {/* Map Background */}
              <div className="relative h-40 overflow-hidden">
                <img
                  src="https://maps.googleapis.com/maps/api/staticmap?center=SKMCH+Gate+No+1,Muzaffarpur,Bihar,842004,India&zoom=15&size=600x200&maptype=roadmap&markers=color:red%7CSKMCH+Gate+No+1,Muzaffarpur,Bihar,842004,India&key=AIzaSyD-9tSrke72PouQMnMX-a7eZSW0jkFMBWY"
                  alt="Map location"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                {/* Fallback map background */}
                <div
                  className="absolute inset-0 hidden items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, #1a2a3a 0%, #0f1923 40%, #162032 70%, #1c2b3a 100%)',
                  }}
                />
                {/* Always-visible map overlay tile pattern */}
                <div className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(135deg, #2d3e50 0%, #1a2a3a 30%, #243447 60%, #1e2d3d 100%)',
                    backgroundImage: `
                      repeating-linear-gradient(0deg, transparent, transparent 28px, rgba(255,255,255,0.04) 28px, rgba(255,255,255,0.04) 29px),
                      repeating-linear-gradient(90deg, transparent, transparent 28px, rgba(255,255,255,0.04) 28px, rgba(255,255,255,0.04) 29px)
                    `
                  }}
                >
                  <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-white/10 -translate-y-4" />
                  <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-white/10 translate-y-6" />
                  <div className="absolute left-1/3 top-0 bottom-0 w-0.5 bg-white/10" />
                  <div className="absolute left-2/3 top-0 bottom-0 w-0.5 bg-white/10" />
                  <div className="absolute inset-0 bg-black/40" />
                </div>
                {/* Pin + text overlay */}
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                  <motion.div
                    animate={{ y: [0, -6, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                    className="mb-2"
                  >
                    <div className="relative">
                      <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg shadow-red-500/60 border-2 border-white">
                        <div className="w-2 h-2 bg-white rounded-full" />
                      </div>
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-6 border-l-transparent border-r-transparent border-t-red-500" />
                    </div>
                  </motion.div>
                  <p className="text-white font-bold text-sm drop-shadow-lg">SKMCH GATE NO:-1, Muzaffarpur, Bihar</p>
                  <p className="text-gray-300 text-xs mt-1 drop-shadow">Click below to open in Google Maps</p>
                </div>
              </div>
              {/* Button bar */}
              <div className="bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur-sm px-4 py-3 flex items-center justify-center border-t border-gray-200 dark:border-gray-700 group-hover:bg-cyan-500/10 transition-colors">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 group-hover:text-cyan-500 transition-colors flex items-center gap-2">
                  <FaMapMarkerAlt className="text-red-500" />
                  Open in Google Maps →
                </span>
              </div>
            </motion.a>
          </motion.div>

          {/* Form Side */}
          <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
            className="lg:col-span-3 bg-gradient-to-br from-gray-50/90 dark:from-gray-900/90 to-white/90 dark:to-black/90 p-8 md:p-12 rounded-3xl border border-gray-200 dark:border-gray-800 backdrop-blur-md">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 ml-1">Full Name</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500"><FaUser /></div>
                    <input type="text" name="name" required value={formData.name} onChange={handleChange}
                      className="w-full pl-12 pr-4 py-4 bg-white/50 dark:bg-black/50 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:border-cyan-500 text-gray-900 dark:text-white transition-all" placeholder="Rahul Sharma" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 ml-1">Email Address</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500"><FaEnvelope /></div>
                    <input type="email" name="email" required value={formData.email} onChange={handleChange}
                      className="w-full pl-12 pr-4 py-4 bg-white/50 dark:bg-black/50 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:border-cyan-500 text-gray-900 dark:text-white transition-all" placeholder="rahul@company.in" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 ml-1">Phone Number</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500"><FaPhone /></div>
                    <input type="tel" name="phone" required value={formData.phone} onChange={handleChange}
                      className="w-full pl-12 pr-4 py-4 bg-white/50 dark:bg-black/50 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:border-cyan-500 text-gray-900 dark:text-white transition-all" placeholder="+91 98765 43210" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 ml-1">What Do You Do?</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500"><FaBriefcase /></div>
                    <select
                      name="role"
                      required
                      value={formData.role}
                      onChange={handleChange}
                      className="w-full pl-12 pr-10 py-4 bg-white/50 dark:bg-black/50 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:border-cyan-500 text-gray-900 dark:text-white transition-all appearance-none cursor-pointer"
                    >
                      <option value="" disabled className="text-gray-400">Select your role…</option>
                      {roleOptions.map(opt => (
                        <option key={opt} value={opt} className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white">{opt}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-gray-500">
                      <FaChevronDown className="text-sm" />
                    </div>
                  </div>
                </div>
                <div className="space-y-3 md:col-span-2 mt-2">
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 ml-1">Services Needed (Select multiple)</label>
                  <div className="flex flex-wrap gap-3">
                    {servicesList.map(s => {
                      const isSelected = formData.services.includes(s);
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => toggleService(s)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                            isSelected 
                              ? 'bg-gradient-to-r from-cyan-500 to-blue-600 border-transparent text-white shadow-lg shadow-cyan-500/30' 
                              : 'bg-white/50 dark:bg-black/50 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-cyan-500'
                          }`}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 ml-1">Additional Information</label>
                <textarea name="message" rows="5" required value={formData.message} onChange={handleChange}
                  className="w-full p-4 bg-white/50 dark:bg-black/50 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:border-cyan-500 text-gray-900 dark:text-white transition-all resize-none" placeholder="Tell us about your project, goals, or any other helpful details..." />
              </div>
              <div className="pt-4 flex flex-col sm:flex-row justify-end items-center gap-6">
                <a 
                  href="https://wa.me/919473448626" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto flex items-center justify-center gap-3 px-10 py-5 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold text-lg tracking-wide shadow-xl shadow-green-500/30 transition-all hover:scale-105 hover:shadow-green-500/50"
                >
                  Chat on WhatsApp
                  <FaWhatsapp className="text-xl" />
                </a>
                <div className="w-full sm:w-auto">
                  <PremiumButton primary large icon={<FaPaperPlane />}>Send Message</PremiumButton>
                </div>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
